/**
 * PluginPanel — panel/section listing all document instances
 * (define / import / content / use / for) with status badges,
 * run/stop actions, and click-to-detail navigation.
 */

import type {
    ContentDefinition,
    ForDefinition,
    ImportBinding,
    ParsedDocument,
    PluginDefinition,
    UseBinding,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import {
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    FileInput,
    FileText,
    ListOrdered,
    Play,
    PlayCircle,
    Puzzle,
    Search,
    Share2,
    Square,
    XCircle,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { useEditorStore } from "../store/editor-store";
import {
    type ContentExecutionStatus,
    type ExecutionStatus,
    type ForExecutionStatus,
    type ImportExecutionStatus,
    type UseExecutionStatus,
    usePluginExecutionStore,
} from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { SidebarCloseButton } from "./SidebarCloseButton";
import { EMPTY_DEFINITIONS } from "./shared";

/* ── Generic status type union ── */
type AnyStatus = ExecutionStatus | ForExecutionStatus | ContentExecutionStatus | ImportExecutionStatus | UseExecutionStatus;

/* ── Compact status badge ── */
function StatusDot({ status }: { status: AnyStatus }) {
    const t = useT("editor");
    switch (status) {
        case "running":
            return (
                <span
                    className="relative inline-flex h-2 w-2 shrink-0"
                    style={getThemeAccentStyle("running")}
                    title={t("pluginPanel.status.running")}
                >
                    <span
                        className="absolute inline-flex h-full w-full animate-ping rounded-full theme-accent-fill opacity-75"
                        style={getThemeAccentStyle("running")}
                    />
                    <span
                        className="relative inline-flex h-2 w-2 rounded-full theme-accent-fill"
                        style={getThemeAccentStyle("running")}
                    />
                </span>
            );
        case "success":
            return (
                <span
                    className="inline-flex shrink-0 theme-accent-text"
                    style={getThemeAccentStyle("success")}
                    title={t("pluginPanel.status.done")}
                >
                    <CheckCircle2 size={11} />
                </span>
            );
        case "error":
            return (
                <span
                    className="inline-flex shrink-0 theme-accent-text"
                    style={getThemeAccentStyle("error")}
                    title={t("common.error")}
                >
                    <XCircle size={11} />
                </span>
            );
        default:
            return null; // idle = no badge, cleaner look
    }
}

/* ── Single instance item ── */
function InstanceItem({
    definition,
    definitions,
    compact = false,
    onSelectInstance,
}: {
    definition: PluginDefinition;
    definitions: Map<string, PluginDefinition>;
    compact?: boolean;
    onSelectInstance?: (id: string) => void;
}) {
    const t = useT("editor");
    const execution = usePluginExecutionStore(
        (s) => s.executions.get(definition.id) ?? null,
    );
    const runPrimary = usePluginExecutionStore((s) => s.runPrimary);
    const cancelExecution = usePluginExecutionStore((s) => s.cancelExecution);
    const plugin = pluginUIRegistry.get(definition.pluginName);
    const selectedInstanceId = useEditorStore((s) => s.selectedInstanceId);
    const [ctxMenu, setCtxMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
    }>({ open: false, x: 0, y: 0 });

    // Fallback: if restoreFromParsedOutput hasn't run yet but the definition has output
    const hasParsedOutput = !execution && !!definition.output;
    const status: ExecutionStatus = execution?.status ?? (hasParsedOutput ? "success" : "idle");
    const isRunning = status === "running";
    const isSelected = selectedInstanceId === definition.id;

    const icon = plugin?.manifest.icon ?? "🧩";
    const title = plugin?.manifest.title ?? definition.pluginName;
    const selectInstance = useCallback(() => {
        const nextId = isSelected ? null : definition.id;
        if (nextId && onSelectInstance) {
            onSelectInstance(nextId);
            return;
        }
        useEditorStore.getState().setSelectedInstanceId(nextId);
    }, [definition.id, isSelected, onSelectInstance]);
    const contextItems = useMemo<ContextMenuItem[]>(
        () => [
            {
                key: "open-detail",
                label: t("pluginPanel.action.openDetail"),
                icon: <Puzzle size={14} />,
                onClick: () => {
                    if (!isSelected) selectInstance();
                },
            },
            {
                key: isRunning ? "stop" : "run",
                label: isRunning ? t("pluginPanel.action.stopRun") : t("pluginPanel.action.run"),
                icon: isRunning ? <Square size={14} /> : <Play size={14} />,
                separator: true,
                disabled: !isRunning && !plugin,
                onClick: () => {
                    if (isRunning) {
                        cancelExecution(definition.id);
                    } else if (plugin) {
                        runPrimary(definition, definitions);
                    }
                },
            },
        ],
        [
            cancelExecution,
            definition,
            definitions,
            isRunning,
            isSelected,
            plugin,
            runPrimary,
            selectInstance,
            t,
        ],
    );

    return (
        <>
            <button
                type="button"
                className={cn(
                    "group flex items-center w-full text-left transition-colors",
                    compact ? "gap-1.5 px-2 py-1 rounded" : "gap-2 px-3 py-2",
                    isSelected ? "bg-primary/10" : "hover:bg-muted/40",
                )}
                onClick={selectInstance}
                onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCtxMenu({ open: true, x: e.clientX, y: e.clientY });
                }}
                title={compact ? `${definition.id} · ${title}` : undefined}
            >
                {/* Plugin icon */}
                <span className={cn("shrink-0 leading-none", compact ? "text-[10px]" : "text-xs")}>{icon}</span>

                {/* Single-line instance row */}
                <div className="flex flex-1 min-w-0 items-center gap-1.5">
                    <span
                        className={cn(
                            "font-medium text-foreground/80 truncate",
                            compact ? "text-[11px] group-hover:hidden" : "text-xs",
                        )}
                    >
                        {definition.id}
                    </span>
                    <span
                        className={cn(
                            "text-[10px] text-muted-foreground/45 truncate",
                            compact ? "hidden group-hover:inline" : "inline",
                        )}
                    >
                        {title}
                    </span>
                    <span className="ml-auto shrink-0">
                        <StatusDot status={status} />
                    </span>
                </div>
            </button>
            <ContextMenu
                open={ctxMenu.open}
                x={ctxMenu.x}
                y={ctxMenu.y}
                items={contextItems}
                onClose={() => setCtxMenu((state) => ({ ...state, open: false }))}
            />
        </>
    );
}

/* ── Collapsible section header ── */
function SectionHeader({
    icon,
    label,
    count,
    open,
    onToggle,
    compact = false,
}: {
    icon: React.ReactNode;
    label: string;
    count: number;
    open: boolean;
    onToggle: () => void;
    compact?: boolean;
}) {
    const chevron = open ? <ChevronDown size={11} /> : <ChevronRight size={11} />;

    return (
        <button
            type="button"
            className={cn(
                "flex items-center gap-1.5 w-full text-[10px] text-muted-foreground uppercase tracking-wider hover:bg-muted/30 transition-colors",
                compact ? "px-2 py-1 rounded" : "px-3 py-1.5 font-semibold",
            )}
            onClick={onToggle}
        >
            {compact && (
                <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                    {chevron}
                </span>
            )}
            {icon}
            <span>{label}</span>
            <span className="text-[9px] font-normal text-muted-foreground/50">{count}</span>
            {!compact && <span className="ml-auto">{chevron}</span>}
        </button>
    );
}

function CompactInstanceRow({
    icon,
    id,
    hoverLabel,
    status,
    title,
    contextItems,
}: {
    icon?: React.ReactNode;
    id: string;
    hoverLabel: string;
    status: AnyStatus;
    title: string;
    contextItems: ContextMenuItem[];
}) {
    const [ctxMenu, setCtxMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
    }>({ open: false, x: 0, y: 0 });

    return (
        <>
            <button
                type="button"
                className="group flex items-center w-full text-left transition-colors gap-1.5 px-2 py-1 rounded hover:bg-muted/40"
                onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCtxMenu({ open: true, x: event.clientX, y: event.clientY });
                }}
                title={title}
            >
                {icon && (
                    <span className="shrink-0 inline-flex items-center justify-center w-3 text-muted-foreground/60">
                        {icon}
                    </span>
                )}
                <div className="flex flex-1 min-w-0 items-center gap-1.5">
                    <span className="font-medium text-foreground/80 truncate text-[11px] group-hover:hidden">
                        {id}
                    </span>
                    <span className="hidden group-hover:inline text-[10px] text-muted-foreground/45 truncate">
                        {hoverLabel}
                    </span>
                    <span className="ml-auto shrink-0">
                        <StatusDot status={status} />
                    </span>
                </div>
            </button>
            <ContextMenu
                open={ctxMenu.open}
                x={ctxMenu.x}
                y={ctxMenu.y}
                items={contextItems}
                onClose={() => setCtxMenu((state) => ({ ...state, open: false }))}
            />
        </>
    );
}

/* ── Import item ── */
function ImportItem({ binding, compact = false }: { binding: ImportBinding; compact?: boolean }) {
    const t = useT("editor");
    const exec = usePluginExecutionStore((s) => s.importExecutions.get(binding.namespace));
    const runImportAction = usePluginExecutionStore((s) => s.runImportAction);
    const status: ImportExecutionStatus = exec?.status ?? "idle";
    const isRunning = status === "running";

    if (compact) {
        return (
            <CompactInstanceRow
                id={binding.namespace}
                hoverLabel={binding.source}
                status={status}
                title={`${binding.namespace} · ${binding.source}`}
                contextItems={[
                    {
                        key: "run",
                        label: isRunning ? t("pluginPanel.status.running") : t("pluginPanel.action.runImport"),
                        icon: <Play size={14} />,
                        disabled: isRunning,
                        onClick: () => runImportAction(binding.namespace),
                    },
                ]}
            />
        );
    }

    return (
        <div className={cn(
            "group flex items-center w-full text-left hover:bg-muted/40 transition-colors",
            compact ? "gap-1.5 px-2 py-1 rounded" : "gap-2 px-3 py-2",
        )}>
            <FileInput size={13} className="shrink-0 text-muted-foreground/60" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground/80 truncate">
                        {binding.namespace}
                    </span>
                    <StatusDot status={status} />
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-muted-foreground/40 truncate">
                        {binding.source}
                    </span>
                </div>
            </div>
            <div
                className={cn(
                    "shrink-0 flex items-center justify-center w-6 h-6 rounded-md transition-all",
                    "opacity-0 group-hover:opacity-100",
                    isRunning
                        ? "theme-accent-icon-button opacity-100"
                        : "text-primary hover:bg-primary/10",
                )}
                style={isRunning ? getThemeAccentStyle("error") : undefined}
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isRunning) runImportAction(binding.namespace);
                }}
                title={isRunning ? t("pluginPanel.status.running") : t("pluginPanel.action.runImport")}
            >
                {isRunning ? (
                    <span
                        className="flex items-center justify-center w-3 h-3 rounded-sm theme-accent-fill"
                        style={getThemeAccentStyle("error")}
                    />
                ) : (
                    <Play size={13} />
                )}
            </div>
        </div>
    );
}

/* ── Content item ── */
function ContentItem({ content, compact = false }: { content: ContentDefinition; compact?: boolean }) {
    const t = useT("editor");
    const exec = usePluginExecutionStore((s) => s.contentExecutions.get(content.id));
    const runContentAction = usePluginExecutionStore((s) => s.runContentAction);
    const status: ContentExecutionStatus = exec?.status ?? "idle";
    const isRunning = status === "running";
    const typeLabel = content.template ? t("pluginPanel.type.template") : t("pluginPanel.type.static");

    if (compact) {
        return (
            <CompactInstanceRow
                id={content.id}
                hoverLabel={typeLabel}
                status={status}
                title={`${content.id} · ${typeLabel}`}
                contextItems={[
                    {
                        key: "run",
                        label: isRunning ? t("pluginPanel.status.running") : t("pluginPanel.action.runContent"),
                        icon: <Play size={14} />,
                        disabled: isRunning,
                        onClick: () => runContentAction(content.id),
                    },
                ]}
            />
        );
    }

    return (
        <div className={cn(
            "group flex items-center w-full text-left hover:bg-muted/40 transition-colors",
            compact ? "gap-1.5 px-2 py-1 rounded" : "gap-2 px-3 py-2",
        )}>
            <FileText size={13} className="shrink-0 text-muted-foreground/60" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground/80 truncate">
                        {content.id}
                    </span>
                    <StatusDot status={status} />
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-muted-foreground/40 truncate">
                        {typeLabel}
                    </span>
                </div>
            </div>
            <div
                className={cn(
                    "shrink-0 flex items-center justify-center w-6 h-6 rounded-md transition-all",
                    "opacity-0 group-hover:opacity-100",
                    isRunning
                        ? "theme-accent-icon-button opacity-100"
                        : "text-primary hover:bg-primary/10",
                )}
                style={isRunning ? getThemeAccentStyle("error") : undefined}
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isRunning) runContentAction(content.id);
                }}
                title={isRunning ? t("pluginPanel.status.running") : t("pluginPanel.action.runContent")}
            >
                {isRunning ? (
                    <span
                        className="flex items-center justify-center w-3 h-3 rounded-sm theme-accent-fill"
                        style={getThemeAccentStyle("error")}
                    />
                ) : (
                    <Play size={13} />
                )}
            </div>
        </div>
    );
}

/* ── Use item ── */
function UseItem({ binding, compact = false }: { binding: UseBinding; compact?: boolean }) {
    const t = useT("editor");
    const exec = usePluginExecutionStore((s) => s.useExecutions.get(binding.id));
    const runUseAction = usePluginExecutionStore((s) => s.runUseAction);
    const status: UseExecutionStatus = exec?.status ?? "idle";
    const isRunning = status === "running";
    const sourceLabel = `← ${binding.source}`;

    if (compact) {
        return (
            <CompactInstanceRow
                id={binding.id}
                hoverLabel={sourceLabel}
                status={status}
                title={`${binding.id} · ${sourceLabel}`}
                contextItems={[
                    {
                        key: "run",
                        label: isRunning ? t("pluginPanel.status.running") : t("pluginPanel.action.runUse"),
                        icon: <Play size={14} />,
                        disabled: isRunning,
                        onClick: () => runUseAction(binding.id),
                    },
                ]}
            />
        );
    }

    return (
        <div className={cn(
            "group flex items-center w-full text-left hover:bg-muted/40 transition-colors",
            compact ? "gap-1.5 px-2 py-1 rounded" : "gap-2 px-3 py-2",
        )}>
            <Share2 size={13} className="shrink-0 text-muted-foreground/60" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground/80 truncate">
                        {binding.id}
                    </span>
                    <StatusDot status={status} />
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-muted-foreground/40 truncate">
                        {sourceLabel}
                    </span>
                </div>
            </div>
            <div
                className={cn(
                    "shrink-0 flex items-center justify-center w-6 h-6 rounded-md transition-all",
                    "opacity-0 group-hover:opacity-100",
                    isRunning
                        ? "theme-accent-icon-button opacity-100"
                        : "text-primary hover:bg-primary/10",
                )}
                style={isRunning ? getThemeAccentStyle("error") : undefined}
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isRunning) runUseAction(binding.id);
                }}
                title={isRunning ? t("pluginPanel.status.running") : t("pluginPanel.action.runUse")}
            >
                {isRunning ? (
                    <span
                        className="flex items-center justify-center w-3 h-3 rounded-sm theme-accent-fill"
                        style={getThemeAccentStyle("error")}
                    />
                ) : (
                    <Play size={13} />
                )}
            </div>
        </div>
    );
}

/* ── For item ── */
function ForItem({ forDef, compact = false }: { forDef: ForDefinition; compact?: boolean }) {
    const t = useT("editor");
    const exec = usePluginExecutionStore((s) => s.forExecutions.get(forDef.id));
    const runForAction = usePluginExecutionStore((s) => s.runForAction);
    const cancelExecution = usePluginExecutionStore((s) => s.cancelExecution);
    const status: ForExecutionStatus = exec?.status ?? "idle";
    const isRunning = status === "running";
    const loopLabel = `each="${forDef.each}" of="${forDef.of}"`;

    if (compact) {
        return (
            <CompactInstanceRow
                id={forDef.id}
                hoverLabel={loopLabel}
                status={status}
                title={`${forDef.id} · ${loopLabel}`}
                contextItems={[
                    {
                        key: isRunning ? "stop" : "run",
                        label: isRunning ? t("pluginPanel.action.stopFor") : t("pluginPanel.action.runFor"),
                        icon: isRunning ? <Square size={14} /> : <Play size={14} />,
                        onClick: () => {
                            if (isRunning) {
                                cancelExecution(forDef.id);
                                return;
                            }
                            runForAction(forDef.id);
                        },
                    },
                ]}
            />
        );
    }

    return (
        <div className={cn(
            "group flex items-center w-full text-left hover:bg-muted/40 transition-colors",
            compact ? "gap-1.5 px-2 py-1 rounded" : "gap-2 px-3 py-2",
        )}>
            <ListOrdered size={13} className="shrink-0 text-muted-foreground/60" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground/80 truncate">
                        {forDef.id}
                    </span>
                    <StatusDot status={status} />
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-muted-foreground/40 truncate">
                        {loopLabel}
                    </span>
                    {exec?.meta && status === "success" ? (
                        <span className="text-[9px] text-muted-foreground/60">
                            {exec.meta.succeeded}/{exec.meta.total}
                        </span>
                    ) : null}
                    {isRunning && exec ? (
                        <span className="text-[9px] text-muted-foreground/60">
                            {exec.completedIterations}/{exec.totalIterations}
                        </span>
                    ) : null}
                </div>
            </div>
            <div
                className={cn(
                    "shrink-0 flex items-center justify-center w-6 h-6 rounded-md transition-all",
                    "opacity-0 group-hover:opacity-100",
                    isRunning
                        ? "theme-accent-icon-button opacity-100"
                        : "text-primary hover:bg-primary/10",
                )}
                style={isRunning ? getThemeAccentStyle("error") : undefined}
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isRunning) {
                        cancelExecution(forDef.id);
                        return;
                    }
                    runForAction(forDef.id);
                }}
                title={isRunning ? t("pluginPanel.action.stopFor") : t("pluginPanel.action.runFor")}
            >
                {isRunning ? (
                    <span
                        className="flex items-center justify-center w-3 h-3 rounded-sm theme-accent-fill"
                        style={getThemeAccentStyle("error")}
                    />
                ) : (
                    <Play size={13} />
                )}
            </div>
        </div>
    );
}

export interface PluginPanelProps {
    embedded?: boolean;
    hideEmbeddedHeader?: boolean;
    compact?: boolean;
    parsedDocumentOverride?: ParsedDocument | null;
    onSelectInstance?: (id: string) => void;
}

/* ── Main Panel ── */
export function PluginPanel({
    embedded = false,
    hideEmbeddedHeader = false,
    compact = false,
    parsedDocumentOverride,
    onSelectInstance,
}: PluginPanelProps = {}) {
    const t = useT("editor");
    const storeParsedDocument = useEditorStore((s) => s.parsedDocument);
    const parsedDocument =
        parsedDocumentOverride === undefined
            ? storeParsedDocument
            : parsedDocumentOverride;
    const runAll = usePluginExecutionStore((s) => s.runAll);
    const cancelRunAll = usePluginExecutionStore((s) => s.cancelRunAll);
    const isRunningAll = usePluginExecutionStore((s) => s.isRunningAll);
    const [searchQuery, setSearchQuery] = useState("");
    const [embeddedExpanded, setEmbeddedExpanded] = useState(false);

    // Section collapse state
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        import: false,
        content: false,
        define: false,
        use: false,
        for: false,
    });
    const toggleSection = (key: string) =>
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

    const definitions = useMemo(
        () => parsedDocument?.definitions ?? EMPTY_DEFINITIONS,
        [parsedDocument],
    );
    const defArray = Array.from(definitions.values());
    const forDefinitions = useMemo(
        () => parsedDocument?.forDefinitions ?? new Map<string, ForDefinition>(),
        [parsedDocument],
    );
    const forArray = Array.from(forDefinitions.values());
    const contentDefinitions = useMemo(
        () => parsedDocument?.contentDefinitions ?? new Map<string, ContentDefinition>(),
        [parsedDocument],
    );
    const contentArray = Array.from(contentDefinitions.values());
    const importBindings = useMemo(
        () => parsedDocument?.imports ?? [],
        [parsedDocument],
    );
    const useBindings = useMemo(
        () => parsedDocument?.uses ?? [],
        [parsedDocument],
    );

    const totalCount =
        defArray.length + forArray.length + contentArray.length + importBindings.length + useBindings.length;

    // Filter all types by search query
    const q = searchQuery.toLowerCase().trim();
    const filteredDefs = useMemo(() => {
        if (!q) return defArray;
        return defArray.filter(
            (def) =>
                def.id.toLowerCase().includes(q) ||
                def.pluginName.toLowerCase().includes(q),
        );
    }, [defArray, q]);

    const filteredImports = useMemo(() => {
        if (!q) return importBindings;
        return importBindings.filter(
            (imp) =>
                imp.namespace.toLowerCase().includes(q) ||
                imp.source.toLowerCase().includes(q),
        );
    }, [importBindings, q]);

    const filteredContent = useMemo(() => {
        if (!q) return contentArray;
        return contentArray.filter(
            (cd) => cd.id.toLowerCase().includes(q),
        );
    }, [contentArray, q]);

    const filteredUses = useMemo(() => {
        if (!q) return useBindings;
        return useBindings.filter(
            (u) =>
                u.id.toLowerCase().includes(q) ||
                u.source.toLowerCase().includes(q),
        );
    }, [useBindings, q]);

    const filteredFors = useMemo(() => {
        if (!q) return forArray;
        return forArray.filter(
            (f) =>
                f.id.toLowerCase().includes(q) ||
                f.each.toLowerCase().includes(q) ||
                f.of.toLowerCase().includes(q),
        );
    }, [forArray, q]);

    const filteredTotal =
        filteredDefs.length + filteredImports.length + filteredContent.length + filteredUses.length + filteredFors.length;

    // Whether we need section grouping or can show flat define list
    const hasMultipleTypes =
        importBindings.length > 0 ||
        contentArray.length > 0 ||
        useBindings.length > 0 ||
        forArray.length > 0;

    if (totalCount === 0) {
        if (embedded) return null;
        return (
            <div className="flex flex-col items-center justify-center gap-3 h-full p-4 text-center">
                <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center">
                    <Puzzle size={18} className="text-muted-foreground/50" />
                </div>
                <div>
                    <p className="text-xs font-medium text-muted-foreground">
                        {t("pluginPanel.empty.noInstances")}
                    </p>
                    <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                        {t("pluginPanel.empty.addHint", { syntax: "::define" })}
                    </p>
                </div>
            </div>
        );
    }

    const actionButton = isRunningAll ? (
        <button
            type="button"
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium theme-accent-icon-button"
            style={getThemeAccentStyle("error")}
            onClick={cancelRunAll}
            title={t("pluginPanel.action.stopRun")}
        >
            <Square size={11} className="fill-current" />
            {t("pluginPanel.action.stop")}
        </button>
    ) : (
        <button
            type="button"
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium theme-accent-icon-button"
            style={getThemeAccentStyle("success")}
            onClick={() => runAll(definitions)}
            title={t("pluginPanel.action.runAllTitle")}
        >
            <PlayCircle size={11} />
            {t("pluginPanel.action.runAll")}
        </button>
    );

    const searchBox = (
        <div className="px-2 py-1.5 border-b border-border/50 shrink-0">
            <div className="flex items-center gap-1.5 bg-muted/50 rounded-md px-2 py-1">
                <Search size={11} className="text-muted-foreground/60 shrink-0" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("pluginPanel.filterPlaceholder")}
                    className="flex-1 bg-transparent text-[11px] text-foreground placeholder:text-muted-foreground/40 outline-none"
                />
                {searchQuery && (
                    <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="text-muted-foreground/60 hover:text-foreground text-[10px]"
                    >
                        ✕
                    </button>
                )}
            </div>
        </div>
    );

    const groupedSectionBodyClass = cn("space-y-0.5", compact && "pl-5");

    const instanceList = (
        <>
            {!hasMultipleTypes ? (
                /* Only defines → flat list (original behavior) */
                <div className="space-y-0.5">
                    {filteredDefs.map((def) => (
                        <InstanceItem
                            key={def.id}
                            definition={def}
                            definitions={definitions}
                            compact={compact}
                            onSelectInstance={onSelectInstance}
                        />
                    ))}
                </div>
            ) : (
                /* Multiple types → grouped sections */
                <>
                    {/* Import section */}
                    {filteredImports.length > 0 && (
                        <div>
                            <SectionHeader
                                icon={<FileInput size={11} />}
                                label={t("pluginPanel.section.import")}
                                count={filteredImports.length}
                                open={openSections.import ?? false}
                                onToggle={() => toggleSection("import")}
                                compact={compact}
                            />
                            {openSections.import === true && (
                                <div className={groupedSectionBodyClass}>
                                    {filteredImports.map((imp) => (
                                        <ImportItem key={imp.namespace} binding={imp} compact={compact} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Content section */}
                    {filteredContent.length > 0 && (
                        <div>
                            <SectionHeader
                                icon={<FileText size={11} />}
                                label={t("pluginPanel.section.content")}
                                count={filteredContent.length}
                                open={openSections.content ?? false}
                                onToggle={() => toggleSection("content")}
                                compact={compact}
                            />
                            {openSections.content === true && (
                                <div className={groupedSectionBodyClass}>
                                    {filteredContent.map((cd) => (
                                        <ContentItem key={cd.id} content={cd} compact={compact} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Define section */}
                    {filteredDefs.length > 0 && (
                        <div>
                            <SectionHeader
                                icon={<Puzzle size={11} />}
                                label={t("pluginPanel.section.define")}
                                count={filteredDefs.length}
                                open={openSections.define ?? false}
                                onToggle={() => toggleSection("define")}
                                compact={compact}
                            />
                            {openSections.define === true && (
                                <div className={groupedSectionBodyClass}>
                                    {filteredDefs.map((def) => (
                                        <InstanceItem
                                            key={def.id}
                                            definition={def}
                                            definitions={definitions}
                                            compact={compact}
                                            onSelectInstance={onSelectInstance}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Use section */}
                    {filteredUses.length > 0 && (
                        <div>
                            <SectionHeader
                                icon={<Share2 size={11} />}
                                label={t("pluginPanel.section.use")}
                                count={filteredUses.length}
                                open={openSections.use ?? false}
                                onToggle={() => toggleSection("use")}
                                compact={compact}
                            />
                            {openSections.use === true && (
                                <div className={groupedSectionBodyClass}>
                                    {filteredUses.map((u) => (
                                        <UseItem key={u.id} binding={u} compact={compact} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* For section */}
                    {filteredFors.length > 0 && (
                        <div>
                            <SectionHeader
                                icon={<ListOrdered size={11} />}
                                label={t("pluginPanel.section.for")}
                                count={filteredFors.length}
                                open={openSections.for ?? false}
                                onToggle={() => toggleSection("for")}
                                compact={compact}
                            />
                            {openSections.for === true && (
                                <div className={groupedSectionBodyClass}>
                                    {filteredFors.map((f) => (
                                        <ForItem key={f.id} forDef={f} compact={compact} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {filteredTotal === 0 && searchQuery && (
                <div className="p-4 text-center text-[11px] text-muted-foreground/60">
                    {t("pluginPanel.noMatches", { query: searchQuery })}
                </div>
            )}
        </>
    );

    if (embedded) {
        if (hideEmbeddedHeader) {
            return (
                <div className={cn("py-0.5", compact && "text-[11px]")}>
                    {!compact && searchBox}
                    <div className={cn(compact && "space-y-0.5")}>{instanceList}</div>
                </div>
            );
        }
        return (
            <div className="border-b border-border/50 pb-1 mb-1">
                <div className="flex h-6 items-center gap-1 px-2 rounded hover:bg-muted/60">
                    <button
                        type="button"
                        onClick={() => setEmbeddedExpanded((v) => !v)}
                        className="flex min-w-0 flex-1 items-center text-left text-xs"
                    >
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                            {embeddedExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </span>
                        <Puzzle
                            size={12}
                            className="text-muted-foreground mr-1.5 shrink-0"
                        />
                        <span className="text-[11px] leading-none text-muted-foreground uppercase tracking-wider truncate">
                            {t("pluginPanel.instances")} ({totalCount})
                        </span>
                    </button>
                    <div onClick={(e) => e.stopPropagation()}>{actionButton}</div>
                </div>
                {embeddedExpanded && (
                    <div className="pt-1">
                        {searchBox}
                        <div>{instanceList}</div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Panel header */}
            <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("pluginPanel.instances")}
                    <span className="ml-1 text-[10px] font-normal text-muted-foreground/60">
                        {totalCount}
                    </span>
                </span>
                <div className="flex items-center gap-1">
                    {actionButton}
                    <SidebarCloseButton />
                </div>
            </div>

            {/* Search */}
            {searchBox}

            {/* Instance list */}
            <div className="flex-1 min-h-0 overflow-auto">
                {instanceList}
            </div>
        </div>
    );
}
